import { FilterOpts } from "pages/OrderPages/OrderListPage/OrderListPage.tsx";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { models } from "types/api/viewModels.ts";
import { nullSafeLocaleCompare } from "utils/string.ts";
import ChipDropdownMenu from "components/OrderSearch/ChipDropdownMenu.tsx";
import { FilterType } from "components/OrderSearch/filterType.ts";
import { LeadAssignmentFiltersContainer } from "./LeadAssignmentFilters.styles.ts";
import { getAssignedToLocation } from "features/LeadGen/utils/leadAssignmentLocation.ts";
import { useAssignedToPersonNameLookup } from "../../hooks";
import {
	getAssignedToPersonName,
	getLeadStatusLabel
} from "features/LeadGen/pages/LeadAssignments/utils/LeadAssignmentsPage.utils.ts";

interface LeadAssignmentFiltersProps {
	filterOptions: FilterOpts;
	setFilterOptions: Dispatch<SetStateAction<FilterOpts>>;
	leads: models["LeadAssignmentViewModel"][];
}

const LeadAssignmentFilters = ({ filterOptions, setFilterOptions, leads }: LeadAssignmentFiltersProps) => {
	const assignedToPersonNameLookup = useAssignedToPersonNameLookup();

	const getUniqueValues = useCallback(
		(mapper: (lead: models["LeadAssignmentViewModel"]) => string | null | undefined) => {
			return leads
				?.map(mapper)
				.filter(
					(
						item: string | null | undefined,
						index: number,
						arr: (string | null | undefined)[]
					): item is string => !!item && arr.indexOf(item) === index
				)
				.sort(nullSafeLocaleCompare);
		},
		[leads]
	);

	const assignedToLocations = useMemo(
		() => getUniqueValues((lead) => getAssignedToLocation(lead)),
		[getUniqueValues]
	);
	const assignedToPeople = useMemo(
		() => getUniqueValues((lead) => getAssignedToPersonName(lead, assignedToPersonNameLookup)),
		[assignedToPersonNameLookup, getUniqueValues]
	);
	const brands = useMemo(() => getUniqueValues((lead) => lead.lead?.brand), [getUniqueValues]);
	const statuses = useMemo(() => getUniqueValues((lead) => getLeadStatusLabel(lead.status)), [getUniqueValues]);

	const onFilterChange = useCallback(
		(id: string, values: string[]) => {
			setFilterOptions((prev: FilterOpts) => ({
				...prev,
				[id]: values
			}));
		},
		[setFilterOptions]
	);

	const getFilterId = (type: string) => `lead-assignments-${type}-filter-chip`;

	const filters = useMemo(
		() => [
			{
				selectedItems: filterOptions?.assignedToLocation ?? [],
				onChange: (v: string[]) => onFilterChange("assignedToLocation", v),
				filterType: FilterType.ASSIGNED_TO_LOCATION,
				items: assignedToLocations ?? [],
				dataTestId: getFilterId("assigned-to"),
				GTMIdentifier: getFilterId("assigned-to")
			},
			{
				selectedItems: filterOptions?.assignedToPerson ?? [],
				onChange: (v: string[]) => onFilterChange("assignedToPerson", v),
				filterType: FilterType.PERSON,
				items: assignedToPeople ?? [],
				dataTestId: getFilterId("person"),
				GTMIdentifier: getFilterId("person")
			},
			{
				selectedItems: filterOptions?.brands ?? [],
				onChange: (v: string[]) => onFilterChange("brands", v),
				filterType: FilterType.BRAND,
				items: brands ?? [],
				dataTestId: getFilterId("brands"),
				GTMIdentifier: getFilterId("brands")
			},
			{
				selectedItems: filterOptions?.status ?? [],
				onChange: (v: string[]) => onFilterChange("status", v),
				filterType: FilterType.LEAD_STATUS,
				items: statuses ?? [],
				dataTestId: getFilterId("statuses"),
				GTMIdentifier: getFilterId("statuses")
			}
		],
		[
			assignedToLocations,
			assignedToPeople,
			brands,
			statuses,
			filterOptions?.assignedToLocation,
			filterOptions?.assignedToPerson,
			filterOptions?.brands,
			filterOptions?.status,
			onFilterChange
		]
	);

	return (
		<LeadAssignmentFiltersContainer>
			{filters.map((filter) => (
				<ChipDropdownMenu
					key={filter.filterType}
					selectedItems={filter.selectedItems}
					onChange={filter.onChange}
					filterType={filter.filterType}
					items={filter.items}
					dataTestId={filter.dataTestId}
					GTMIdentifier={filter.GTMIdentifier}
				/>
			))}
		</LeadAssignmentFiltersContainer>
	);
};

export default LeadAssignmentFilters;
